// MSSQL renderer-side column + CHECK-constraint introspection. Sibling to
// `mssqlSchemaIntrospection.ts` (FK edges); split out to keep each file under
// the 200-line budget. The bulk-mode queries here power the snapshot
// extension in Slice A3.5 (PRD: Ralph/JsonColumnSemantics/PRD.md § A3.5) so
// `MssqlTableSnapshot.tables[*].columns` + `checkConstraints` populate from
// paged catalog reads rather than N+1 per table.
//
// CHECK clauses are returned verbatim as `sys.check_constraints.definition`
// strings. The JSON detector at the projector layer (`detectMssqlIsJsonColumns`)
// handles unwrapping `CHECK ( … )` / quoted identifiers / compound-clause
// rejection — this module only carries clause text + ownership routing.
//
// S7 item 6 — the columns query reads `sys.columns ⋈ sys.types ⋈ sys.objects
// ⋈ sys.schemas` with the PK flag from `sys.index_columns ⋈ sys.indexes`,
// replacing the old INFORMATION_SCHEMA.COLUMNS ⋈ KEY_COLUMN_USAGE ⋈
// TABLE_CONSTRAINTS ⋈ sys.columns pile. It was the wall-clock leader of the
// snapshot build: three name-joined views plus a sort by schema/table name.
// The catalog form pages off the `sys.columns` clustered key
// `(object_id, column_id)` instead, so every page is an index seek.
//
// The output shape is byte-identical to the old query's (asserted against
// AdventureWorks by `mssql-columns-query-parity.realDb.spec.ts`), which needs
// three deliberate translations:
//   - `DATA_TYPE` — `COALESCE(bt.name, ut.name)`: INFORMATION_SCHEMA reports
//     the BASE type for an alias type (`Name` → `nvarchar`, `sysname` →
//     `nvarchar`) but its own name for CLR system types (`geography`). The
//     alias test is `ut.user_type_id <> ut.system_type_id` (true for both
//     alias types and `sysname`); the base row is looked up by
//     `ut.system_type_id`, which does not exist for the CLR types (240) — the
//     LEFT JOIN then leaves `bt` NULL and the COALESCE keeps `geography`.
//   - `CHARACTER_MAXIMUM_LENGTH` — `sys.columns.max_length` is BYTES; the view
//     reports CHARACTERS, so n-types are halved and `-1` (`(max)`) passes
//     through. Only the length-suffix types matter downstream
//     (`assembleColumnType`), everything else is NULL as in the view.
//   - `ORDINAL_POSITION` — `ROW_NUMBER() OVER (PARTITION BY object_id ORDER BY
//     column_id)`, not `column_id`: dropping a column leaves a GAP in
//     `column_id` while the view renumbers 1..n.
// `IS_NULLABLE` is emitted as the view's `'YES'`/`'NO'` text so the row
// aggregator below is untouched.

import { fetchMssqlPagedArtefact, type PagedFetchOptions } from './mssqlIntrospectionQuery';
import type { IntrospectionRow, PagedIntrospectionOutcome } from './introspectionPaging';
import { CHECK_ROW_BUDGET, COLUMN_ROW_BUDGET } from './introspectionBudgets';

// TOTAL row budgets across all pages (S7 item 4) — memory bounds, NOT
// per-request caps; see `introspectionBudgets.ts`.
export type MssqlColumnIntrospection = {
  readonly schema: string;
  readonly table: string;
  readonly name: string;
  readonly dataType: string;
  readonly nullable: boolean;
  readonly isPrimaryKey: boolean;
  /** `sys.columns.is_identity` — an `IDENTITY(seed, incr)` column. Not a legal
   *  INSERT target unless the session sets `IDENTITY_INSERT` on. Optional so a
   *  caller (or fixture) that predates the flag reads as "not known". */
  readonly isIdentity?: boolean;
  /** `sys.columns.is_computed` — a `col AS <expr>` computed column. Never a
   *  legal INSERT target. Optional for the same reason as `isIdentity`. */
  readonly isComputed?: boolean;
  readonly ordinal: number;
};

export type MssqlCheckIntrospection = {
  readonly schema: string;
  readonly table: string;
  readonly definition: string;
};

/** Sink handed one page's worth of aggregated rows (S7 item 1's phase 2). */
export type MssqlColumnPageSink = (
  columns: readonly MssqlColumnIntrospection[],
) => void | Promise<void>;

export type MssqlCheckPageSink = (
  checks: readonly MssqlCheckIntrospection[],
) => void | Promise<void>;

/** Catalog columns query, paged by `(object_id, column_id)`. See the header. */
export function buildMssqlColumnsPageSql(offsetFetch: string): string {
  return `WITH cols AS (
  SELECT
    c.object_id,
    c.column_id,
    c.name,
    c.is_nullable,
    c.is_identity,
    c.is_computed,
    c.max_length,
    COALESCE(bt.name, ut.name) AS type_name
  FROM sys.columns c
  JOIN sys.types ut ON ut.user_type_id = c.user_type_id
  LEFT JOIN sys.types bt
    ON ut.user_type_id <> ut.system_type_id
   AND ut.system_type_id <> 240
   AND bt.user_type_id = ut.system_type_id
)
SELECT
  s.name AS TABLE_SCHEMA,
  o.name AS TABLE_NAME,
  cols.name AS COLUMN_NAME,
  cols.type_name AS DATA_TYPE,
  CASE
    WHEN cols.max_length = -1 THEN -1
    WHEN cols.type_name IN ('nchar', 'nvarchar') THEN cols.max_length / 2
    WHEN cols.type_name IN ('char', 'varchar', 'binary', 'varbinary') THEN cols.max_length
    ELSE NULL
  END AS CHARACTER_MAXIMUM_LENGTH,
  CASE WHEN cols.is_nullable = 1 THEN 'YES' ELSE 'NO' END AS IS_NULLABLE,
  ROW_NUMBER() OVER (PARTITION BY cols.object_id ORDER BY cols.column_id) AS ORDINAL_POSITION,
  CASE WHEN pk.column_id IS NOT NULL THEN 1 ELSE 0 END AS IS_PRIMARY_KEY,
  CASE WHEN cols.is_identity = 1 THEN 1 ELSE 0 END AS IS_IDENTITY,
  CASE WHEN cols.is_computed = 1 THEN 1 ELSE 0 END AS IS_COMPUTED
FROM cols
JOIN sys.objects o ON o.object_id = cols.object_id
JOIN sys.schemas s ON s.schema_id = o.schema_id
LEFT JOIN (
  SELECT ic.object_id, ic.column_id
  FROM sys.index_columns ic
  JOIN sys.indexes i
    ON i.object_id = ic.object_id AND i.index_id = ic.index_id
  WHERE i.is_primary_key = 1
) pk ON pk.object_id = cols.object_id AND pk.column_id = cols.column_id
WHERE o.type IN ('U', 'V')
ORDER BY cols.object_id, cols.column_id
${offsetFetch}`;
}

/**
 * Page every column of every user table/view into `sink`, one page at a time.
 * Resolves when the catalog is exhausted, a page query fails, or
 * `COLUMN_ROW_BUDGET` is spent (which reports the `columns` truncation).
 */
export async function fetchMssqlColumnPages(
  connectionId: string,
  database: string,
  sink: MssqlColumnPageSink,
  options?: PagedFetchOptions,
): Promise<void> {
  await fetchMssqlPagedArtefact<MssqlColumnIntrospection>({
    connectionId,
    database,
    label: 'cols',
    artefact: 'columns',
    sqlForPage: buildMssqlColumnsPageSql,
    aggregate: aggregateColumnRows,
    sink,
    totalBudget: COLUMN_ROW_BUDGET,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating convenience wrapper over `fetchMssqlColumnPages`. */
export async function fetchMssqlColumns(
  connectionId: string,
  database: string,
): Promise<MssqlColumnIntrospection[]> {
  const out: MssqlColumnIntrospection[] = [];
  await fetchMssqlColumnPages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

/** CHECK clauses for every user table, paged by `sys.check_constraints.object_id`. */
export function buildMssqlChecksPageSql(offsetFetch: string): string {
  return `SELECT
  s.name AS TABLE_SCHEMA,
  t.name AS TABLE_NAME,
  cc.definition AS DEFINITION
FROM sys.check_constraints cc
JOIN sys.objects t ON cc.parent_object_id = t.object_id
JOIN sys.schemas s ON t.schema_id = s.schema_id
WHERE t.type = 'U' AND cc.definition IS NOT NULL
ORDER BY cc.object_id
${offsetFetch}`;
}

export async function fetchMssqlCheckConstraintPages(
  connectionId: string,
  database: string,
  sink: MssqlCheckPageSink,
  options?: PagedFetchOptions,
): Promise<PagedIntrospectionOutcome> {
  return fetchMssqlPagedArtefact<MssqlCheckIntrospection>({
    connectionId,
    database,
    label: 'checks',
    artefact: 'checks',
    sqlForPage: buildMssqlChecksPageSql,
    aggregate: aggregateCheckRows,
    sink,
    totalBudget: CHECK_ROW_BUDGET,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating convenience wrapper over `fetchMssqlCheckConstraintPages`. */
export async function fetchMssqlCheckConstraints(
  connectionId: string,
  database: string,
): Promise<MssqlCheckIntrospection[]> {
  const out: MssqlCheckIntrospection[] = [];
  await fetchMssqlCheckConstraintPages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

type ColumnRowKeys = {
  readonly TABLE_SCHEMA: unknown;
  readonly TABLE_NAME: unknown;
  readonly COLUMN_NAME: unknown;
  readonly DATA_TYPE: unknown;
  readonly CHARACTER_MAXIMUM_LENGTH: unknown;
  readonly IS_NULLABLE: unknown;
  readonly ORDINAL_POSITION: unknown;
  readonly IS_PRIMARY_KEY: unknown;
  readonly IS_IDENTITY: unknown;
  readonly IS_COMPUTED: unknown;
};

/**
 * Base types that carry a `(N)` / `(max)` length suffix. The column query
 * reports `CHARACTER_MAXIMUM_LENGTH` for these in *characters* (no byte
 * halving for n-types) and as `-1` for `(max)`. Other types (text/ntext/image
 * also report a length, numerics report none) keep their bare base — the
 * length is not part of how downstream consumers categorize them (e.g. enum
 * eligibility only inspects the short-string length, and the resolver never
 * string-matches on the full type).
 */
const LENGTH_SUFFIX_TYPES: ReadonlySet<string> = new Set([
  'char',
  'nchar',
  'varchar',
  'nvarchar',
  'binary',
  'varbinary',
]);

/** Fold a `bit` / `0|1` / `'1'` / boolean flag column into a boolean. */
function isTruthyFlag(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value.trim() === '1' || value.trim().toLowerCase() === 'true';
  return false;
}

function assembleColumnType(baseType: string, charMaxLength: unknown): string {
  if (!LENGTH_SUFFIX_TYPES.has(baseType.toLowerCase())) return baseType;
  if (charMaxLength === null || charMaxLength === undefined || charMaxLength === '') return baseType;
  const len = Number(charMaxLength);
  if (!Number.isFinite(len)) return baseType;
  return len === -1 ? `${baseType}(max)` : `${baseType}(${len})`;
}

type CheckRowKeys = {
  readonly TABLE_SCHEMA: unknown;
  readonly TABLE_NAME: unknown;
  readonly DEFINITION: unknown;
};

/**
 * Pure aggregator — exported so unit tests exercise the row-shape conversion
 * without standing up the IPC bridge. Mirrors `aggregateFkRows`.
 */
export function aggregateColumnRows(
  rows: readonly IntrospectionRow[],
): MssqlColumnIntrospection[] {
  const out: MssqlColumnIntrospection[] = [];
  for (const raw of rows) {
    const row = raw as ColumnRowKeys;
    const schema = String(row.TABLE_SCHEMA ?? '').trim();
    const table = String(row.TABLE_NAME ?? '').trim();
    const name = String(row.COLUMN_NAME ?? '').trim();
    if (!schema || !table || !name) continue;
    const dataType = assembleColumnType(String(row.DATA_TYPE ?? '').trim(), row.CHARACTER_MAXIMUM_LENGTH);
    const nullable = String(row.IS_NULLABLE ?? '').toUpperCase() !== 'NO';
    const ordinal = Number(row.ORDINAL_POSITION ?? 0);
    const isPrimaryKey = Number(row.IS_PRIMARY_KEY ?? 0) === 1;
    // `CASE WHEN … THEN 1 ELSE 0 END` normally arrives as a number, but a
    // driver that surfaces `bit` as a boolean (tedious does) and one that
    // stringifies it must both fold to the same flag.
    const isIdentity = isTruthyFlag(row.IS_IDENTITY);
    const isComputed = isTruthyFlag(row.IS_COMPUTED);
    out.push({
      schema, table, name, dataType, nullable, isPrimaryKey, isIdentity, isComputed, ordinal,
    });
  }
  return out;
}

export function aggregateCheckRows(
  rows: readonly IntrospectionRow[],
): MssqlCheckIntrospection[] {
  const out: MssqlCheckIntrospection[] = [];
  for (const raw of rows) {
    const row = raw as CheckRowKeys;
    const schema = String(row.TABLE_SCHEMA ?? '').trim();
    const table = String(row.TABLE_NAME ?? '').trim();
    const definition = String(row.DEFINITION ?? '').trim();
    if (!schema || !table || !definition) continue;
    out.push({ schema, table, definition });
  }
  return out;
}
